/**
 * The 10 sport-neutral ranks (economy v2): levels group 10-per-rank.
 * Badge art: final user-designed set, 512px WebP served from the CDN
 * (assets-cdn.mdc: no bundled require() — it silently breaks on the iOS
 * production build profile). Sources live in `assets/Badges/` for re-upload
 * via `server/scripts/uploadRankBadges.mjs`. Escalation: bronze → silver →
 * gold → platinum → diamond materials, then winged crowns up to Icon.
 *
 * Names describe a career stage, not a head-to-head result. The ladder is now
 * derived from a player's PEAK coin balance (specs/coin-based-ranks), so it
 * measures coin achievement (a high-water mark, only-up) rather than tenure.
 * A skill-based name at the summit would overclaim (and would collide with a
 * future RP/LP ladder).
 */

import type { ImageSourcePropType } from 'react-native';
import { ASSET_PATHS, getAssetUrl } from '../../../constants/assets';

export interface RankDef {
  /** 1-based rank index (1 = Rookie … 10 = Icon). */
  index: number;
  name: string;
  badge: ImageSourcePropType;
}

export const LEVELS_PER_RANK = 10;
export const TOP_RANK_INDEX = 10;

/**
 * Accent colour per rank, following the badge art's own material ramp (bronze → silver → gold →
 * platinum → diamond → winged crowns). Used where a surface tints itself to a player's rank — an
 * avatar ring, a chip — so the colour and the crest always tell the same story.
 *
 * Fixed hexes rather than theme tokens on purpose: a rank means the same thing in any theme, and
 * these are chosen to read on the dark app background. Index 1..10; use getRankAccentColor().
 */
export const RANK_ACCENT_COLORS: readonly string[] = [
  '#D6A883', // 1  Rookie    — bronze
  '#CBD5E1', // 2  Prospect  — silver
  '#FCD34D', // 3  Contender — gold
  '#C4B5FD', // 4  Challenger— platinum
  '#6EE7B7', // 5  Veteran   — diamond
  '#7DD3FC', // 6  All-Star
  '#FCA5A5', // 7  Champion
  '#F0ABFC', // 8  Superstar
  '#FDBA74', // 9  Legend
  '#FDE68A', // 10 Icon
] as const;

export const RANKS: RankDef[] = [
  { index: 1, name: 'Rookie', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_01_ROOKIE) } },
  { index: 2, name: 'Prospect', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_02_PROSPECT) } },
  { index: 3, name: 'Contender', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_03_CONTENDER) } },
  { index: 4, name: 'Challenger', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_04_CHALLENGER) } },
  { index: 5, name: 'Veteran', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_05_VETERAN) } },
  { index: 6, name: 'All-Star', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_06_ALL_STAR) } },
  { index: 7, name: 'Champion', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_07_CHAMPION) } },
  { index: 8, name: 'Superstar', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_08_SUPERSTAR) } },
  { index: 9, name: 'Legend', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_09_LEGEND) } },
  // Asset key/filename stays `rank-10-goat` — the CDN object is cached for a year
  // and renaming it would only cost a purge for no user-visible gain.
  { index: 10, name: 'Icon', badge: { uri: getAssetUrl(ASSET_PATHS.BADGES.RANK_10_GOAT) } },
];
